import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import supabase from "./supabase";
import { parseISO } from "date-fns";
import ShareApp from "./components/share-app";
import { recordVisit, recordEngagement } from "./visit-tracking";
import NotFound from "./components/not-found";
import Stats from "./components/stats";
import "./style.css";
import { CATEGORIES, prayerCategories, tagStyle } from "./categories";

const TYPES = ["Prayer Request", "Praise Report"];
const TABLE = "prayer-app-db";
const COMMENTS = "prayer_comments";
const count = (value) => Number(value) || 0;

// Older rows used Anonymous; newer hidden posts use a type-specific name.
// This is display compatibility, not authenticated ownership.
const isHidden = prayer => ["anonymous", "hidden prayer", "hidden praise"].includes(String(prayer.userName || "").trim().toLowerCase());
const authorName = prayer => isHidden(prayer)
  ? (prayer.requestType === "Praise Report" ? "Hidden Praise" : "Hidden Prayer")
  : prayer.userName;

function Icon({ name, ...props }) {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    search: (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    comment: (
      <path d="M20 15a3 3 0 0 1-3 3H9l-5 3v-5a3 3 0 0 1-2-3V7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3z" />
    ),
    close: <path d="m6 6 12 12M18 6 6 18" />,
    back: <path d="m14 5-7 7 7 7" />,
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    down: <path d="m6 9 6 6 6-6" />,
  };
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {paths[name]}
    </svg>
  );
}

function Dialog({ title, onClose, children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    function keydown(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const items = ref.current?.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select, a[href], [tabindex="0"]',
        );
        if (!items?.length) return;
        const first = items[0],
          last = items[items.length - 1];
        if (
          e.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          last.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === ref.current)
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", keydown);
      previous?.focus();
    };
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`dialog ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        tabIndex="-1"
        ref={ref}
      >
        <div className="dialog-navigation">
          <button className="back-button" onClick={onClose}>
            <Icon name="back" width="16" />
            Back
          </button>
          <button
            className="icon-button"
            aria-label="Close dialog"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <h1 id="dialog-title">{title}</h1>
        {children}
      </section>
    </div>
  );
}

function RequestForm({ onClose, onPosted }) {
  const [form, setForm] = useState({
    userName: "",
    userChurch: "",
    message: "",
    requestType: TYPES[0],
    category: [],
  });
  const [anonymous, setAnonymous] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const lock = useRef(false);
  const change = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  async function submit(e) {
    e.preventDefault();
    if (lock.current) return;
    if ((!anonymous && !form.userName.trim()) || !form.message.trim() || !form.category.length) {
      setError(anonymous ? "Please add your message and at least one hashtag." : "Please add your first name, message, and at least one hashtag.");
      return;
    }
    lock.current = true;
    setPending(true);
    setError("");
    try {
      const { data, error: failure } = await supabase
        .from(TABLE)
        .insert([
          {
            ...form,
// Preserve the original text-column API while allowing up to three tags.
            category: form.category.join(", "),
// Strip identifying fields before sending hidden posts, not just when rendering.
            userName: anonymous ? (form.requestType === "Praise Report" ? "Hidden Praise" : "Hidden Prayer") : form.userName.trim(),
            userChurch: anonymous ? "" : form.userChurch.trim(),
            message: form.message.trim(),
            date: new Date().toISOString(),
          },
        ])
        .select();
      if (failure || !data?.[0])
        throw failure || new Error("No prayer returned");
      void recordEngagement(form.requestType === "Praise Report" ? "praise" : "prayer");
      onPosted(data[0]);
      onClose();
    } catch {
      setError(
        "Your prayer couldn’t be posted. Your words are saved here—please try again.",
      );
    } finally {
      lock.current = false;
      setPending(false);
    }
  }
  return (
    <Dialog title="Share Prayer or Praise" onClose={onClose}>
      <p className="dialog-intro">
        Share a prayer request or celebrate what God has done with a praise report. We’re here to pray and celebrate with you.
      </p>
      <form className="request-form" onSubmit={submit}>
        <fieldset>
          <legend>
            What would you like to share? <span className="required">*</span>
          </legend>
          <div className="type-choices">
            {TYPES.map((type, i) => (
              <label
                className={form.requestType === type ? "selected" : ""}
                key={type}
              >
                <input
                  type="radio"
                  name="requestType"
                  value={type}
                  checked={form.requestType === type}
                  onChange={change}
                />
                <span>{i ? "🙌" : "🙏"}</span>
                {type}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="anonymous-option">
          <label><span className="hidden-request-emoji" aria-hidden="true">🙈</span><span>Hidden request</span><input type="checkbox" checked={anonymous} onChange={event => setAnonymous(event.target.checked)} aria-describedby="anonymous-help" /></label>
          <p id="anonymous-help">Hide my name and church. My post will still be public.</p>
        </div>
        {!anonymous && <>
        <label>
          Name <span className="required">*</span>
          <input
            name="userName"
            autoComplete="name"
            required
            maxLength="80"
            value={form.userName}
            onChange={change}
            placeholder="Your name"
          />
        </label>
        <label>
          Do you go to a church? (Optional)
          <input
            name="userChurch"
            maxLength="160"
            value={form.userChurch}
            onChange={change}
            placeholder="Your church name"
          />
        </label>
        </>}
        <label>
          {form.requestType === "Praise Report" ? "What would you like to praise God for?" : "How can we pray for you?"} <span className="required">*</span>
          <textarea
            name="message"
            required
            maxLength="1000"
            value={form.message}
            onChange={change}
            placeholder="Share as much or as little as you’d like..."
          />
          <span className="character-count">{form.message.length}/1000</span>
        </label>
        <fieldset>
          <legend>
            Hashtags <span className="required">*</span>
          </legend>
          <p className="hashtag-hint" id="hashtag-hint">Choose up to 3 · {form.category.length}/3 selected</p>
          <div className="category-choices">
            {CATEGORIES.map((category) => (
              <label
                key={category}
                style={tagStyle(category)}
                className={`${form.category.includes(category) ? "selected" : ""} ${form.category.length >= 3 && !form.category.includes(category) ? "at-limit" : ""}`}
              >
                <input
                  type="checkbox"
                  name="category"
                  value={category}
                  aria-describedby="hashtag-hint"
                  disabled={form.category.length >= 3 && !form.category.includes(category)}
                  checked={form.category.includes(category)}
                  onChange={() => setForm(prev => ({ ...prev, category: prev.category.includes(category) ? prev.category.filter(tag => tag !== category) : prev.category.length < 3 ? [...prev.category, category] : prev.category }))}
                />
                {category}
              </label>
            ))}
          </div>
        </fieldset>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button className="black-button submit-button" disabled={pending}>
          {pending ? (form.requestType === "Praise Report" ? "Posting your praise…" : "Posting your prayer…") : (form.requestType === "Praise Report" ? "Post Your Praise" : "Post Your Prayer")}
        </button>
      </form>
    </Dialog>
  );
}

function CommentDialog({ prayer, onClose, onAdded }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [comingSoon, setComingSoon] = useState(false);
  const [available, setAvailable] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const lock = useRef(false);
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      setComingSoon(false);
      try {
        const { data, error: failure } = await supabase
          .from(COMMENTS)
          .select("*")
          .eq("prayer_id", prayer.id)
          .order("created_at", { ascending: true });
        if (failure) throw failure;
        if (active) {
          setComments(data || []);
          setAvailable(true);
        }
      } catch (failure) {
        if (active) {
          setComingSoon(["PGRST205", "42P01"].includes(failure?.code));
          setError("We can’t load comments just now. Please check back a little later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [prayer.id, retry]);
  async function submit(e) {
    e.preventDefault();
    if (lock.current || !name.trim() || !message.trim()) return;
    lock.current = true;
    setPending(true);
    setError("");
    try {
      const { data, error: failure } = await supabase
        .from(COMMENTS)
        .insert([
          {
            prayer_id: prayer.id,
            author_name: name.trim(),
            message: message.trim(),
          },
        ])
        .select();
      if (failure || !data?.[0])
        throw failure || new Error("No comment returned");
      setComments((prev) => [...prev, data[0]]);
      setMessage("");
      onAdded(prayer.id);
    } catch {
      setError("Your comment couldn’t be posted. Please try again.");
    } finally {
      lock.current = false;
      setPending(false);
    }
  }
  return (
    <Dialog
      title="Words of encouragement"
      onClose={onClose}
      className="comments-dialog"
    >
      <p className="dialog-intro">
        For {authorName(prayer)}'s{" "}
        {prayer.requestType === "Praise Report"
          ? "praise report"
          : "prayer request"}
      </p>
      <blockquote>{prayer.message}</blockquote>
      {loading ? (
        <p role="status">Loading comments…</p>
      ) : (
        <div className="comment-list">
          {comments.map((comment) => (
            <article className="comment" key={comment.id}>
              <strong>{comment.author_name}</strong>
              <p>{comment.message}</p>
            </article>
          ))}
          {available && !comments.length && (
            <p className="muted">
              Be the first to share a word of encouragement.
            </p>
          )}
        </div>
      )}
      {error && (available ? <p className="error" role="alert">{error}</p> :
        <div className="comments-unavailable">
          <p role={comingSoon ? "status" : "alert"}><span className="notice-emoji" aria-hidden="true">💬</span><span>{comingSoon ? "Comments are on the way!!" : "Comments aren’t available right now. Please try again later."}</span></p>
          {!comingSoon && <button className="comments-retry" onClick={() => setRetry(value => value + 1)}>Try again</button>}
        </div>
      )}
      {available && (
        <form className="request-form comment-form" onSubmit={submit}>
          <label>
            Name
            <input
              required
              maxLength="80"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
          </label>
          <label>
            Your comment
            <textarea
              required
              maxLength="1000"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <button className="black-button" disabled={pending}>
            {pending ? "Posting…" : "Post Comment"}
          </button>
        </form>
      )}
    </Dialog>
  );
}

// Reaction flags discourage repeat clicks in this browser only.
// They are not a server-side guarantee of one reaction per person.
function PrayerCard({ prayer, setPrayers, onComments, commentCount }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const lock = useRef(false);
  const [reactions, setReactions] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem(`prayer-reactions:${prayer.id}`)) || {}
      );
    } catch {
      return {};
    }
  });
  async function react(column) {
    if (lock.current || reactions[column]) return;
    lock.current = true;
    setPending(true);
    setError("");
    try {
      const { data, error: failure } = await supabase
        .from(TABLE)
        .update({ [column]: count(prayer[column]) + 1 })
        .eq("id", prayer.id)
        .select();
      if (failure || !data?.[0])
        throw failure || new Error("No reaction returned");
      setPrayers((prev) =>
        prev.map((item) => (item.id === prayer.id ? data[0] : item)),
      );
      void recordEngagement("reaction");
      const next = { ...reactions, [column]: true };
      setReactions(next);
      try {
        localStorage.setItem(
          `prayer-reactions:${prayer.id}`,
          JSON.stringify(next),
        );
      } catch {
        /* Reactions still work when browser storage is unavailable. */
      }
    } catch {
      setError("We couldn’t save your reaction. Please try again.");
    } finally {
      lock.current = false;
      setPending(false);
    }
  }
  const tags = prayerCategories(prayer.category);
  const date = parseISO(String(prayer.date || ""));
  const formatted = Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
  return (
    <article className="prayer-card">
      <header className="card-header">
        <span className="avatar" aria-hidden="true">
          {(authorName(prayer) || "?").charAt(0).toUpperCase()}
        </span>
        <div className="author">
          <strong>{authorName(prayer)}</strong>
          <div className={`metadata ${tags.length > 1 ? "metadata-with-church" : ""}`}>
            <time
              dateTime={
                Number.isNaN(date.getTime()) ? undefined : date.toISOString()
              }
            >
              {formatted}
            </time>
            <span>·</span>
            <span>{prayer.requestType}</span>
            {tags.length > 1 && !isHidden(prayer) && prayer.userChurch?.trim() && <><span>·</span><span className="metadata-church">{prayer.userChurch.trim()}</span></>}
          </div>
          {tags.length === 1 && !isHidden(prayer) && prayer.userChurch?.trim() && <p className="author-church"><span aria-hidden="true">⛪</span> {prayer.userChurch.trim()}</p>}
        </div>
        <div className={`card-tags ${tags.length > 1 ? "multiple-tags" : ""}`} aria-label="Hashtags">
          {tags.map(tag => <span key={tag} className="category-pill" style={tagStyle(tag)}>{tag}</span>)}
        </div>
        <details className="card-menu">
          <summary aria-label={`More options for ${authorName(prayer)}'s prayer`}>
            •••
          </summary>
          <div className="card-menu-content">
            <button onClick={() => onComments(prayer)}>View comments</button>
          </div>
        </details>
      </header>
      <p className={`prayer-message ${expanded ? "expanded" : ""}`}>
        {prayer.message}
      </p>
      {prayer.message?.length > 150 && (
        <button
          className="read-more"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      <footer className="card-actions">
        <button
          className={`prayed-button ${reactions.praying ? "has-reacted" : ""}`}
          disabled={pending || reactions.praying}
          onClick={() => react("praying")}
          aria-label={prayer.requestType === "Praise Report" ? `Amen, ${count(prayer.praying)} amens` : `I prayed, ${count(prayer.praying)} prayers`}
        >
          <span>
            🙏 <b>{prayer.requestType === "Praise Report" ? "Amen" : "I prayed"}</b>
          </span>
          <strong>{count(prayer.praying)}</strong>
        </button>
        <button
          className="reaction-button"
          disabled={pending || reactions.liked_prayer}
          onClick={() => react("liked_prayer")}
          aria-label={prayer.requestType === "Praise Report" ? `Celebrate, ${count(prayer.liked_prayer)} celebrations` : `Like, ${count(prayer.liked_prayer)} likes`}
        >
          {prayer.requestType === "Praise Report" ? "🥳" : "👍"} <span>{count(prayer.liked_prayer)}</span>
        </button>
        <button
          className="reaction-button"
          disabled={pending || reactions.hug}
          onClick={() => react("hug")}
          aria-label={`Hug, ${count(prayer.hug)} hugs`}
        >
          🤗 <span>{count(prayer.hug)}</span>
        </button>
        <button
          className="comments-button"
          onClick={() => onComments(prayer)}
          aria-label={`Comments for ${authorName(prayer)}${commentCount == null ? "" : `, ${commentCount} comments`}`}
        >
          <Icon name="comment" />
          <span>
            {commentCount ?? ""}
            <span className="comments-label">
              {commentCount == null
                ? "Comments"
                : ` comment${commentCount === 1 ? "" : "s"}`}
            </span>
          </span>
        </button>
      </footer>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}

export default function App() {
  // Migrate bookmarks from the old HashRouter before mounting BrowserRouter.
  // Preserve the restored stats route; other legacy hashes return home.
  if (window.location.hash.startsWith("#/")) {
    window.history.replaceState(null, "", (window.location.hash === "#/stats" ? "/stats" : "/") + window.location.search);
  }
  return <BrowserRouter><AppContent /></BrowserRouter>;
}

function AppContent() {
  const pathname = useLocation().pathname;
  const statsPage = pathname === "/stats";
  const notFoundPage = pathname !== "/" && !statsPage;
  useEffect(() => {
    if (statsPage || notFoundPage) return;
// Track home-page activity only; admin checks and 404 visits should not inflate totals.
    let lastCheck = 0;
    const track = () => {
      if (document.visibilityState === "hidden" || Date.now() - lastCheck < 10000) return;
      lastCheck = Date.now();
      void recordVisit();
    };
    track();
    window.addEventListener("pointerdown", track);
    window.addEventListener("keydown", track);
    window.addEventListener("scroll", track, { passive: true });
    document.addEventListener("visibilitychange", track);
    return () => {
      window.removeEventListener("pointerdown", track);
      window.removeEventListener("keydown", track);
      window.removeEventListener("scroll", track);
      document.removeEventListener("visibilitychange", track);
    };
  }, [statsPage, notFoundPage]);
  const menuRef = useRef(null);
  useEffect(() => {
    const closeOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) menuRef.current.open = false;
    };
    const closeOnEscape = event => {
      if (event.key === "Escape" && menuRef.current?.open) {
        menuRef.current.open = false;
        menuRef.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [tab, setTab] = useState("All");
  const [category, setCategory] = useState("All Categories");
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [commentPrayer, setCommentPrayer] = useState(null);
  const [commentCounts, setCommentCounts] = useState(null);
  const [notice, setNotice] = useState("");
  const closeForm = useCallback(() => setFormOpen(false), []);
  const closeComments = useCallback(() => setCommentPrayer(null), []);
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const { data, error: failure } = await supabase
          .from(TABLE)
          .select("*")
          // The database date is day-only: IDs break ties for same-day submissions.
          .order("date", { ascending: false })
          .order("id", { ascending: false })
          .limit(1000);
        if (failure) throw failure;
        if (active) setPrayers(data || []);
      } catch {
        if (active)
          setError("We couldn’t load the prayer feed. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
      try {
        const { data, error: failure } = await supabase
          .from("prayer_comment_counts")
          .select("prayer_id,comment_count");
        if (!failure && active)
          setCommentCounts(
            Object.fromEntries(
              (data || []).map((row) => [
                row.prayer_id,
                count(row.comment_count),
              ]),
            ),
          );
      } catch {
        /* The feed remains available before the comments migration is installed. */
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [reload]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 5000);
    return () => clearTimeout(timer);
  }, [notice]);
  const categories = useMemo(
    () => [
      ...new Set([
        ...CATEGORIES,
        ...prayers.flatMap((p) => prayerCategories(p.category)),
      ]),
    ],
    [prayers],
  );
  const filtered = prayers.filter(
    (p) =>
      (tab === "All" ||
        p.requestType === (tab === "Prayer Requests" ? TYPES[0] : TYPES[1])) &&
      (category === "All Categories" ||
        prayerCategories(p.category).includes(category)) &&
      `${p.userName} ${p.message} ${p.userChurch || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  function posted(prayer) {
    setPrayers((prev) => [prayer, ...prev]);
    setTab("All");
    setCategory("All Categories");
    setQuery("");
    setNotice(prayer.requestType === "Praise Report" ? "Your praise report has been shared with the community." : "Your prayer has been shared with the community.");
  }
  return (
      <div className={`app-shell ${statsPage ? "stats-shell" : notFoundPage ? "not-found-shell" : ""}`}>
        <header className="site-header">
          <Link className="brand" to="/" aria-label="Pray With Me home">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" />
            <div className="brand-copy"><span>PRAY WITH ME</span></div>
          </Link>
          <button
            className="black-button desktop-cta"
            onClick={() => setFormOpen(true)}
          >
            <Icon name="plus" />
            Request Prayer
          </button>
          <ShareApp compact onNotice={setNotice} />
          <details className="site-menu" ref={menuRef}>
            <summary aria-label="Open navigation">
              <Icon name="menu" />
            </summary>
            <nav>
              <Link to="/" onClick={() => { menuRef.current.open = false; }}>Prayer feed</Link>
              <ShareApp onNotice={setNotice} />
              <button
                className="mobile-filter-toggle"
                onClick={(e) => {
                  setMobileFiltersOpen((prev) => !prev);
                  e.currentTarget.closest("details").open = false;
                }}
                aria-expanded={mobileFiltersOpen}
              >
                Filter & search
              </button>
            </nav>
          </details>
        </header>
        <p className="header-scripture">“For God so loved the world, that he gave his only Son, that whoever believes in him should not perish but have eternal life.” <cite>John 3:16</cite></p>
        <Routes>
          <Route
            path="/"
            element={
              <main>
                <div
                  className={`feed-toolbar ${mobileFiltersOpen ? "show-mobile-filters" : ""}`}
                >
                  <div
                    className="tabs"
                    role="tablist"
                    aria-label="Request type"
                  >
                    {["All", "Prayer Requests", "Praise Reports"].map(
                      (value, i, tabs) => (
                        <button
                          key={value}
                          role="tab"
                          aria-selected={tab === value}
                          tabIndex={tab === value ? 0 : -1}
                          onClick={() => setTab(value)}
                          onKeyDown={(e) => {
                            const next =
                              e.key === "ArrowRight"
                                ? (i + 1) % 3
                                : e.key === "ArrowLeft"
                                  ? (i + 2) % 3
                                  : null;
                            if (next !== null) {
                              e.preventDefault();
                              setTab(tabs[next]);
                              e.currentTarget.parentElement.children[
                                next
                              ].focus();
                            }
                          }}
                        >
                          {value}
                        </button>
                      ),
                    )}
                  </div>
                  <span className="toolbar-divider" />
                  <label className="category-filter">
                    <span className="sr-only">Filter by category</span>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option>All Categories</option>
                      {categories.map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                    <Icon name="down" width="18" />
                  </label>
                  <span className="toolbar-divider" />
                  <button
                    className="icon-button search-toggle"
                    aria-label="Search prayers"
                    aria-expanded={searchOpen}
                    onClick={() => setSearchOpen((prev) => !prev)}
                  >
                    <Icon name="search" />
                  </button>
                </div>
                {searchOpen && (
                  <div className="search-field">
                    <Icon name="search" />
                    <input
                      autoFocus
                      type="search"
                      aria-label="Search prayers"
                      placeholder="Search prayers, names, or churches"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                )}
                <div className="feed" aria-busy={loading}>
                  {loading ? (
                    <div className="feed-state" role="status">
                      Loading prayers…
                    </div>
                  ) : error ? (
                    <div className="feed-state">
                      <p role="alert">{error}</p>
                      <button
                        className="black-button"
                        onClick={() => setReload((prev) => prev + 1)}
                      >
                        Try again
                      </button>
                    </div>
                  ) : filtered.length ? (
                    filtered.map((prayer) => (
                      <PrayerCard
                        key={prayer.id}
                        prayer={prayer}
                        setPrayers={setPrayers}
                        onComments={setCommentPrayer}
                        commentCount={
                          commentCounts ? commentCounts[prayer.id] || 0 : null
                        }
                      />
                    ))
                  ) : (
                    <div className="feed-state">
                      <h2>
                        {prayers.length
                          ? "No prayers found"
                          : "A place to pray together"}
                      </h2>
                      <p>
                        {prayers.length
                          ? "Try another category, tab, or search."
                          : "Share what’s on your heart with the community."}
                      </p>
                      <button
                        className="black-button"
                        onClick={() => setFormOpen(true)}
                      >
                        Request Prayer
                      </button>
                    </div>
                  )}
                </div>
              </main>
            }
          />
          {/* Deliberately public but unlisted. No menu link is NOT access control. */}
          <Route path="/stats" element={<Stats lists={prayers} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <button
          className="black-button mobile-cta"
          onClick={() => setFormOpen(true)}
        >
          <Icon name="plus" />
          Request Prayer
        </button>
        <footer className="app-footer">
          <div className="footer-share"><ShareApp compact onNotice={setNotice} /></div>
          <small>© {new Date().getFullYear()} Deared. All rights reserved.</small>
        </footer>
        {notice && (
          <div className="toast" role="status">
            {notice}
          </div>
        )}
        {formOpen && <RequestForm onClose={closeForm} onPosted={posted} />}
        {commentPrayer && (
          <CommentDialog
            prayer={commentPrayer}
            onClose={closeComments}
            onAdded={(id) =>
              setCommentCounts((prev) =>
                prev ? { ...prev, [id]: (prev[id] || 0) + 1 } : prev,
              )
            }
          />
        )}
      </div>
  );
}
